/**
 * F2-c: sezione Camere hotel (owner).
 *
 * Tabella camere (codice / piano / edificio / tipologia / stato) con:
 *  - Aggiungi camera (modal): codice, piano, edificio, tipologia
 *  - Modifica camera (modal): stessi campi + stato
 *  - Disattiva camera (soft-delete via active=false, conferma)
 *
 * Mutations su /owner/hotels/:tenantId/rooms e /owner/rooms/:roomId, con
 * invalidate della query detail. Stile coerente con la CamereSection reception.
 */
import { BedDouble, Pencil, Plus, Trash2, X } from '@hotel-manager/ui/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError } from '../../../api/client.js';
import {
  type HotelRoom,
  type HotelRoomType,
  createHotelRoom,
  patchHotelRoom,
} from '../../../api/owner.js';

interface Props {
  tenantId: string;
  rooms: HotelRoom[];
  roomTypes: HotelRoomType[];
}

const STATUS_LABEL: Record<HotelRoom['status'], string> = {
  free: 'Libera',
  reserved: 'Prenotata',
  occupied: 'Occupata',
  dirty: 'Da pulire',
  out_of_order: 'Fuori servizio',
};

export function CamereSection({ tenantId, rooms, roomTypes }: Props) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<HotelRoom | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HotelRoom | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['owner', 'hotel', tenantId] });

  const deleteMutation = useMutation({
    mutationFn: (roomId: string) => patchHotelRoom(roomId, { active: false }),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      setErrMsg(null);
    },
    onError: (err: unknown) => setErrMsg(formatErr(err)),
  });

  const typeLabel = (id: string) => roomTypes.find((t) => t.id === id)?.label ?? '—';
  const sorted = [...rooms].sort((a, b) =>
    a.code.localeCompare(b.code, undefined, { numeric: true }),
  );

  return (
    <section data-testid="camere-section" className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-text-primary flex items-center gap-1.5">
            <BedDouble size={16} strokeWidth={1.75} className="text-accent-primary" />
            Camere ({sorted.length})
          </h2>
          <p className="text-xs text-text-secondary mt-0.5">
            Gestisci le camere; assegna tipologia, piano ed edificio.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          data-testid="room-add"
          disabled={roomTypes.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg bg-accent-primary text-text-on-accent px-4 h-10 text-sm font-semibold hover:bg-accent-primary-hover transition-colors disabled:opacity-50"
        >
          <Plus size={15} strokeWidth={2} /> Aggiungi camera
        </button>
      </header>

      <div className="bg-bg-card border border-border-base rounded-2xl overflow-hidden shadow-hm-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-base text-text-secondary text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Camera</th>
                <th className="text-left px-4 py-3 font-semibold">Piano</th>
                <th className="text-left px-4 py-3 font-semibold">Edificio</th>
                <th className="text-left px-4 py-3 font-semibold">Tipologia</th>
                <th className="text-left px-4 py-3 font-semibold">Stato</th>
                <th className="px-4 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-text-muted">
                    Nessuna camera. Aggiungi la prima.
                  </td>
                </tr>
              )}
              {sorted.map((room) => (
                <tr
                  key={room.id}
                  className="border-t border-border-base"
                  data-testid={`row-room-${room.code}`}
                >
                  <td className="px-4 py-3 font-mono font-bold text-text-primary">{room.code}</td>
                  <td className="px-4 py-3 text-text-secondary">{room.floor}</td>
                  <td className="px-4 py-3 text-text-secondary">{room.building}</td>
                  <td className="px-4 py-3 text-text-secondary">{typeLabel(room.roomTypeId)}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={room.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => setEditing(room)}
                        data-testid={`room-edit-${room.code}`}
                        className="p-1.5 rounded-md hover:bg-bg-muted text-text-secondary"
                        aria-label="Modifica"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(room)}
                        data-testid={`room-delete-${room.code}`}
                        className="p-1.5 rounded-md hover:bg-[#FAEAE8] text-text-secondary hover:text-[#A8453A]"
                        aria-label="Disattiva"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <RoomEditModal
          tenantId={tenantId}
          room={editing === 'new' ? null : editing}
          roomTypes={roomTypes}
          onClose={() => setEditing(null)}
          onSaved={() => {
            invalidate();
            setEditing(null);
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={`Disattivare la camera ${deleteTarget.code}?`}
          body="La camera non sarà più disponibile per nuove prenotazioni. Potrai riattivarla in seguito."
          confirmLabel="Disattiva"
          pending={deleteMutation.isPending}
          error={errMsg}
          onCancel={() => {
            setDeleteTarget(null);
            setErrMsg(null);
          }}
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        />
      )}
    </section>
  );
}

function StatusPill({ status }: { status: HotelRoom['status'] }) {
  const tone =
    status === 'free'
      ? 'bg-[#eef5e9] text-[#5F8A50]'
      : status === 'out_of_order'
        ? 'bg-[#FAEAE8] text-[#A8453A]'
        : 'bg-bg-muted text-text-secondary';
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${tone}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

function RoomEditModal({
  tenantId,
  room,
  roomTypes,
  onClose,
  onSaved,
}: {
  tenantId: string;
  room: HotelRoom | null;
  roomTypes: HotelRoomType[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = room === null;
  const [code, setCode] = useState(room?.code ?? '');
  const [floor, setFloor] = useState(room?.floor ?? '');
  const [building, setBuilding] = useState(room?.building ?? '');
  const [roomTypeId, setRoomTypeId] = useState(room?.roomTypeId ?? roomTypes[0]?.id ?? '');
  const [status, setStatus] = useState<HotelRoom['status']>(room?.status ?? 'free');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      isNew
        ? createHotelRoom(tenantId, { code, floor, building, roomTypeId })
        : patchHotelRoom(room.id, { code, floor, building, roomTypeId, status }),
    onSuccess: onSaved,
    onError: (err: unknown) => setErrMsg(formatErr(err)),
  });

  return (
    <ModalShell
      title={isNew ? 'Aggiungi camera' : `Modifica camera ${room.code}`}
      onClose={onClose}
    >
      <form
        data-testid="room-modal"
        onSubmit={(e) => {
          e.preventDefault();
          setErrMsg(null);
          mutation.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <ModalField label="Numero / codice">
            <input
              data-testid="room-field-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              maxLength={32}
              className={modalInputCls}
            />
          </ModalField>
          <ModalField label="Piano">
            <input
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              required
              maxLength={64}
              className={modalInputCls}
            />
          </ModalField>
        </div>
        <ModalField label="Edificio">
          <input
            value={building}
            onChange={(e) => setBuilding(e.target.value)}
            required
            maxLength={64}
            className={modalInputCls}
          />
        </ModalField>
        <ModalField label="Tipologia">
          <select
            data-testid="room-field-type"
            value={roomTypeId}
            onChange={(e) => setRoomTypeId(e.target.value)}
            className={modalInputCls}
          >
            {roomTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </ModalField>
        {!isNew && (
          <ModalField label="Stato">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as HotelRoom['status'])}
              className={modalInputCls}
            >
              {(Object.keys(STATUS_LABEL) as HotelRoom['status'][]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </ModalField>
        )}
        {errMsg && (
          <p role="alert" className="text-sm text-[#A8453A]">
            {errMsg}
          </p>
        )}
        <ModalActions
          onCancel={onClose}
          pending={mutation.isPending}
          confirmLabel={isNew ? 'Crea camera' : 'Salva'}
        />
      </form>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Modal primitives condivisi (locali alla sezione owner)
// ---------------------------------------------------------------------------

export function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-bg-card border border-border-base shadow-hm-md">
        <header className="flex items-center justify-between px-5 py-4 border-b border-border-base">
          <h3 className="text-base font-bold text-text-primary">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="p-1.5 rounded-md hover:bg-bg-muted text-text-secondary"
          >
            <X size={16} />
          </button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function ModalField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: il control e' passato come children (associazione implicita).
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-text-secondary">{label}</span>
      {children}
    </label>
  );
}

export function ModalActions({
  onCancel,
  pending,
  confirmLabel,
}: {
  onCancel: () => void;
  pending: boolean;
  confirmLabel: string;
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-1">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg border border-border-base bg-bg-card px-4 h-10 text-sm font-medium text-text-secondary hover:bg-bg-muted transition-colors"
      >
        Annulla
      </button>
      <button
        type="submit"
        disabled={pending}
        className="rounded-lg bg-accent-primary text-text-on-accent px-4 h-10 text-sm font-semibold hover:bg-accent-primary-hover transition-colors disabled:opacity-50"
      >
        {pending ? 'Salvataggio…' : confirmLabel}
      </button>
    </div>
  );
}

export function ConfirmModal({
  title,
  body,
  confirmLabel,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ModalShell title={title} onClose={onCancel}>
      <p className="text-sm text-text-secondary">{body}</p>
      {error && (
        <p role="alert" className="text-sm text-[#A8453A] mt-3">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-2 pt-5">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-border-base bg-bg-card px-4 h-10 text-sm font-medium text-text-secondary hover:bg-bg-muted transition-colors"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={pending}
          data-testid="confirm-danger"
          className="rounded-lg bg-[#A8453A] text-white px-4 h-10 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {pending ? '…' : confirmLabel}
        </button>
      </div>
    </ModalShell>
  );
}

export const modalInputCls =
  'w-full rounded-lg border border-border-input bg-bg-card px-3 h-10 text-sm text-text-primary outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary transition-colors';

export function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'code_taken') return 'Codice camera già in uso.';
    if (err.code === 'room_type_not_found') return 'Tipologia non valida.';
    if (err.code === 'email_or_username_taken') return 'Email o username già in uso.';
    return `Errore (${err.status}).`;
  }
  return 'Errore di rete. Riprova.';
}
